import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Nav
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import AppConstants from "../../AppConstants";
import useAuthToken from "../../hooks/useAuthToken";
import Header from "../../components/Header";
import iconSubscribe from "../../images/icon-subscribe.png";
import "./style.scss";

interface User {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
}

const SubscriptionDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { userToken, ready } = useAuthToken();
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"active" | "inactive">("active");
  const [noSubscription, setNoSubscription] = useState(false);
  const [planDetailsFeatures, setPlanDetailsFeatures] = useState<any>(null);
  const [isPageLoading, setIsPageLoading] = useState(true);

  // Single state for all license users API response data
  const [subscriptionUsers, setSubscriptionUsers] = useState({
    subscriptionId: "",
    subscriptionHealth: {
      status: "active",
      ownerActive: true,
      hasActiveMembers: true,
      totalMembers: 0,
      activeMembers: 0
    },
    owner: {
      ownerId: "",
      firstName: "",
      lastName: "",
      email: "",
      active: true,
      isBlocked: false
    },
    members: [],
    userRole: "owner",
    isCurrentUserOwner: true
  });

  // Single state for all license usage data
  const [licenseData, setLicenseData] = useState({
    subscriptionId: "",
    status: "",
    autoRenew: true,
    maxUsers: 0,
    activeUsers: 0,
    pendingInvitations: 0,
    availableLicenses: 0,
    currentPeriodStart: "",
    currentPeriodEnd: "",
    currentPlanPrice: 0.0,
    planName: "Standard Plan",
    downgradeStatus: { isPending: false },
    role: "owner",
    isDeletedAccount: false,
    isBlockedAccount: false,
    cancelAt: null
  });

  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: ""
  });

  // Function to fetch license usage data (extracted for reuse)
  const fetchLicenseUsage = async () => {
    if (!ready) return;

    try {
      const response = await axios.get(AppConstants.LICENSE_USAGE_API, {
        headers: {
          Authorization: `Bearer ${userToken}`
        }
      });

      // Update state with API response data
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setLicenseData({
          subscriptionId: data.subscriptionId || "",
          status: data.status || "",
          autoRenew: data.autoRenew || true,
          maxUsers: data.maxUsers || 4,
          activeUsers: data.activeUsers || 2,
          pendingInvitations: data.pendingInvitations || 0,
          availableLicenses: data.availableLicenses || 1,
          currentPeriodStart: data.currentPeriodStart || "",
          currentPeriodEnd: data.currentPeriodEnd || "",
          currentPlanPrice: data.currentPlanPrice || 4.0,
          planName: data.planName || "Standard Plan",
          downgradeStatus: data.downgradeStatus || { isPending: false },
          role: data.role || "owner",
          isDeletedAccount: data.isDeletedAccount || false,
          isBlockedAccount: data.isBlockedAccount || false,
          cancelAt: data.cancelAt || null
        });

        setNoSubscription(false); // User has active subscription
      }
    } catch (error: any) {
      console.error("❌ Error fetching license usage:", error);
      if (error.response) {
        console.error("❌ Error Response Status:", error.response.status);
        console.error("❌ Error Response Data:", error.response.data);

        // Check if it's a 404 with "No subscriptions found" message
        if (
          error.response.status === 404 &&
          error.response.data &&
          error.response.data.success === false &&
          error.response.data.error === "No subscriptions found for this user"
        ) {
          console.log("📋 No subscription found for user (404 response)");
          setNoSubscription(true);
          return; // Exit early, don't treat this as an error
        }
      }
      // For other errors, you might want to show an error state
      console.error("❌ Unexpected error:", error);
    }
  };

  // Fetch license usage data on component load
  useEffect(() => {
    if (!ready) return;
    setIsPageLoading(true);
    const fetchAll = async () => {
      await Promise.all([
        fetchLicenseUsage(),
        fetchPlanDetails(),
        fetchLicenseUsers()
      ]);
      setIsPageLoading(false);
    };
    fetchAll();
    // eslint-disable-next-line
  }, [ready, userToken]); // Wait for auth hook to be ready

  // Helper function to format date to MM/DD/YYYY
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      });
    } catch {
      return "N/A";
    }
  };

  // Fetch plan details features (for use in loader logic)
  const fetchPlanDetails = async () => {
    if (!ready) return;
    try {
      const response = await axios.get(AppConstants.PLAN_DETAILS_FEATURES_API, {
        headers: {
          Authorization: `Bearer ${userToken}`
        }
      });
      // Store the plan details features
      if (response.data.success && response.data.data) {
        setPlanDetailsFeatures(response.data.data);
      }
    } catch (error: any) {
      // error handling
    }
  };

  // Function to fetch license users (extracted for reuse)
  const fetchLicenseUsers = async () => {
    if (!ready) return;

    try {
      const response = await axios.get(AppConstants.LICENSE_USERS_API, {
        headers: {
          Authorization: `Bearer ${userToken}`
        }
      });

      // Store the license users data
      if (response.data.success && response.data.data) {
        setSubscriptionUsers({
          subscriptionId: response.data.data.subscriptionId || "",
          subscriptionHealth: response.data.data.subscriptionHealth || {
            status: "active",
            ownerActive: true,
            hasActiveMembers: true,
            totalMembers: 0,
            activeMembers: 0
          },
          owner: response.data.data.owner || {
            ownerId: "",
            firstName: "",
            lastName: "",
            email: "",
            active: true,
            isBlocked: false
          },
          members: response.data.data.members || [],
          userRole: response.data.data.userRole || "owner",
          isCurrentUserOwner: response.data.data.isCurrentUserOwner || true
        });
      } else {
        console.log(
          "❌ License users API response not successful or no data:",
          response.data
        );
      }
    } catch (error: any) {
      console.error("❌ Error fetching license users:", error);
      if (error.response) {
        console.error(
          "❌ License Users Error Response Status:",
          error.response.status
        );
        console.error(
          "❌ License Users Error Response Data:",
          error.response.data
        );
      }
    }
  };

  // fetchLicenseUsers is now called in the loader effect above

  // Function to get combined users list (owner first, then members)
  const getCombinedUsers = () => {
    const combinedUsers = [];

    // Always add owner first (always green)
    if (subscriptionUsers.owner.ownerId) {
      combinedUsers.push({
        id: subscriptionUsers.owner.ownerId,
        name: `${subscriptionUsers.owner.firstName} ${subscriptionUsers.owner.lastName}`,
        email: subscriptionUsers.owner.email,
        isActive: subscriptionUsers.owner.active,
        isOwner: true
      });
    }

    // Add members
    subscriptionUsers.members.forEach((member: any) => {
      combinedUsers.push({
        id: member.memberId,
        name: `${member.firstName} ${member.lastName}`,
        email: member.email,
        isActive: member.active,
        isOwner: false
      });
    });

    return combinedUsers;
  };

  // Function to clear the form data
  const clearFormData = () => {
    setNewUser({ firstName: "", lastName: "", email: "" });
  };

  // Function to check if user can add more users
  const checkUserLimitAndShowModal = () => {
    const currentUserCount = getCombinedUsers().length;
    const maxUsers = licenseData.maxUsers;

    if (currentUserCount >= maxUsers) {
      // Show popup asking if they want to upgrade
      Swal.fire({
        title: "User Limit Reached",
        text: "To add more users - please upgrade the plan.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#28a745"
      }).then((result) => {
        if (result.isConfirmed) {
          // Redirect to upgrade plan component
          navigate("/upgrade-plan");
        }
      });
    } else {
      // Show the add user modal
      setShowAddUserModal(true);
    }
  };

  const handleAddUser = async () => {
    if (newUser.firstName && newUser.email) {
      try {
        const response = await axios.post(
          AppConstants.LICENSE_USER_INVITE,
          {
            email: newUser.email,
            firstName: newUser.firstName,
            lastName: newUser.lastName
          },
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (response.data.success) {
          // Clear form and close modal
          clearFormData();
          setShowAddUserModal(false);

          // Show success toast
          toast.success(response.data.message || "User invited successfully!", {
            position: "top-right",
            autoClose: 3000
          });

          // Refresh users data by calling LICENSE_USERS_API
          await fetchLicenseUsers();
        } else {
          // Show error toast for unsuccessful response
          toast.error(response.data.message || "Failed to invite user", {
            position: "top-right",
            autoClose: 3000
          });
        }
      } catch (error: any) {
        console.error("❌ Error inviting user:", error);

        let errorMessage = "Failed to invite user";
        let isLicenseAvailabilityIssue = false;

        if (error.response && error.response.data) {
          // Check for error message in either 'message' or 'error' field
          errorMessage =
            error.response.data.message ||
            error.response.data.error ||
            "Failed to invite user";

          // Check if it's a license availability issue
          if (errorMessage.toLowerCase().includes("no available licenses")) {
            isLicenseAvailabilityIssue = true;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        // Show appropriate toast based on error type
        if (isLicenseAvailabilityIssue) {
          toast.error(errorMessage, {
            position: "top-right",
            autoClose: 4000
          });
        } else {
          toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000
          });
        }

        // Clear form data after showing error
        clearFormData();
      }
    } else {
      // Show validation error
      toast.error("Please fill in all required fields", {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const toggleUserStatus = async (userId: string) => {
    // Find the user from combined users list
    const combinedUsers = getCombinedUsers();
    const user = combinedUsers.find((u) => u.id === userId);

    if (!user || user.isOwner) {
      // Don't allow toggling owner status or if user not found
      return;
    }

    const newActiveStatus = !user.isActive;

    try {
      const response = await axios.patch(
        `${AppConstants.MEMBER_STATUS_UPDATE_API}/${userId}/status`,
        {
          active: newActiveStatus
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        // Show success toast
        toast.success(
          `Member ${user.isActive ? "deactivated" : "activated"} successfully!`,
          {
            position: "top-right",
            autoClose: 3000
          }
        );

        // Refresh users data by calling LICENSE_USERS_API to get updated status
        await fetchLicenseUsers();
      } else {
        // Show error toast for unsuccessful response
        toast.error(response.data.message || "Failed to update user status", {
          position: "top-right",
          autoClose: 3000
        });
      }
    } catch (error: any) {
      console.error("❌ Error updating user status:", error);

      let errorMessage = "Failed to update user status";
      if (error.response && error.response.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.error ||
          "Failed to update user status";
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show error toast
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  // Show loading state until auth is ready
  if (!ready) {
    return (
      <div className="subscription-dashboard">
        <Header
          title="Subscription Details"
          showMenu={true}
          currentPage="subscription"
        />
        <div className="dashboard-body">
          <Container className="px-0 py-3">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Initializing...</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  // Show no subscription screen when user has no subscription
  if (noSubscription) {
    return (
      <div className="subscription-dashboard">
        <Header
          title="Subscription Details"
          showMenu={true}
          currentPage="subscription"
        />
        <div className="dashboard-body">
          <Container className="px-0 py-3">
            <Row className="mb-4 justify-content-center">
              <Col xl={10} lg={10} md={12} sm={12}>
                <div className="text-center py-5">
                  {/* Success Icon */}
                  <div className="mb-4">
                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        backgroundColor: "#e8f5e8",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto",
                        marginBottom: "24px"
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#28a745",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "20px"
                        }}
                      >
                        ✓
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3
                    className="mb-3"
                    style={{ color: "#28a745", fontWeight: "600" }}
                  >
                    Subscription Features
                  </h3>

                  {/* Subtitle */}
                  <p
                    className="mb-4"
                    style={{ color: "#666", fontSize: "16px" }}
                  >
                    You can now access the subscription
                  </p>

                  {/* Features List */}
                  <div
                    className="text-start mb-4"
                    style={{ maxWidth: "300px", margin: "0 auto" }}
                  >
                    {planDetailsFeatures && planDetailsFeatures.details ? (
                      planDetailsFeatures.details.map(
                        (feature: string, index: number) => (
                          <div
                            className="d-flex align-items-center mb-3"
                            key={index}
                          >
                            <div
                              className="me-3"
                              style={{ color: "#28a745", fontSize: "18px" }}
                            >
                              ✓
                            </div>
                            <span>{feature}</span>
                          </div>
                        )
                      )
                    ) : (
                      <div
                        style={{
                          minHeight: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <div
                          className="spinner-border text-success"
                          style={{ width: "10px", height: "10px" }}
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Continue Button */}
                  <Button
                    className="py-3 px-5"
                    style={{
                      backgroundColor: "#28a745",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "white",
                      width: "100%",
                      maxWidth: "300px"
                    }}
                    onClick={() => navigate("/create-plan")}
                  >
                    Continue
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    );
  }

  // Show member screen when user is a member (not owner)
  if (
    licenseData.role === "member" ||
    subscriptionUsers.userRole === "member"
  ) {
    return (
      <div className="subscription-dashboard">
        <Header
          title="Subscription Details"
          showMenu={true}
          currentPage="subscription"
        />
        <div className="dashboard-body">
          <Container className="px-0 py-3">
            <Row className="mb-4 justify-content-center">
              <Col xl={10} lg={10} md={12} sm={12}>
                <div className="text-center py-5">
                  {/* Subscription Status Card */}
                  <Card
                    className="mb-4"
                    style={{
                      backgroundColor: "#28a745",
                      border: "none",
                      borderRadius: "16px"
                    }}
                  >
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="mb-0 text-white">Subscription Status</h5>
                        <span
                          className="badge px-3 py-1"
                          style={{
                            backgroundColor: "#fc9d01",
                            color: "white",
                            fontSize: "12px",
                            borderRadius: "20px"
                          }}
                        >
                          Active
                        </span>
                      </div>

                      <div
                        className="p-3 rounded"
                        style={{ backgroundColor: "rgba(255,255,255,0.9)" }}
                      >
                        <p
                          className="mb-2"
                          style={{ fontSize: "14px", color: "#333" }}
                        >
                          You are currently added by
                        </p>
                        <p
                          className="mb-2 fw-bold"
                          style={{ fontSize: "16px", color: "#333" }}
                        >
                          {subscriptionUsers.owner.firstName}{" "}
                          {subscriptionUsers.owner.lastName}
                        </p>
                        <p
                          className="mb-0"
                          style={{ fontSize: "14px", color: "#666" }}
                        >
                          {subscriptionUsers.owner.email}
                        </p>
                        <p
                          className="mb-0 mt-3"
                          style={{ fontSize: "14px", color: "#333" }}
                        >
                          Expires On:{" "}
                          <strong>
                            {formatDate(licenseData.currentPeriodEnd) ||
                              "April 5, 2025"}
                          </strong>
                        </p>
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Continue Button */}
                  <Button
                    className="py-3 px-5"
                    style={{
                      backgroundColor: "#28a745",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "white",
                      width: "100%",
                      maxWidth: "300px"
                    }}
                    onClick={() => navigate("/create-plan")}
                  >
                    I would like to choose my own plan
                  </Button>

                  {/* Back To App Button */}
                  <div className="text-center mt-4 mb-3">
                    <Button
                      variant="outline-success"
                      className="px-4 py-2"
                      style={{
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        borderColor: "#28a745",
                        color: "#28a745"
                      }}
                      onClick={() => {
                        window.location.replace(
                          "pretrip://subscription-success"
                        );
                      }}
                    >
                      Back To App
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div className="subscription-dashboard">
        <Header
          title="Subscription Details"
          showMenu={true}
          currentPage="subscription"
        />
        <div className="dashboard-body">
          <Container className="px-0 py-3">
            <div
              style={{
                minHeight: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <div
                className="spinner-border text-success"
                style={{ width: "2rem", height: "2rem", borderWidth: "2px" }}
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-dashboard">
      <Header
        title="Subscription Details"
        showMenu={true}
        currentPage="subscription"
        // subscriptionData={{
        //   autoRenew: licenseData.autoRenew,
        //   planName: licenseData.planName
        // }}
        onDowngradePlan={() => {
          // Navigate to upgrade plan or show downgrade modal
          console.log("Downgrade plan clicked");
        }}
        onCancelSubscription={async () => {
          // Refresh license data after cancellation to get updated cancelAt
          await fetchLicenseUsage();
          console.log("Cancel subscription completed - data refreshed");
        }}
      />

      <div className="dashboard-body">
        <Container className="px-0 py-3">
          {/* Current Plan Card */}
          <Row className="mb-4 justify-content-md-center">
            <Col xl={10} lg={10} md={12} sm={12}>
              <Card
                className="plan-card mb-4"
                style={{ borderRadius: "16px", border: "none" }}
              >
                <Card.Body
                  className="p-4"
                  style={{ backgroundColor: "#e8ebf2", borderRadius: "16px" }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-3"></div>

                  <Card
                    className="plan-details mb-4"
                    style={{
                      background:
                        "linear-gradient(65.07deg, #08A45D 1.12%, #0BBE6D 98.88%)",
                      border: "none",
                      borderRadius: "12px"
                    }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center">
                        <div
                          className="plan-icon me-3"
                          style={{
                            width: "50px",
                            height: "50px",
                            backgroundColor: "#fc9d01",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "20px"
                          }}
                        >
                          <img
                            src={iconSubscribe}
                            alt="Subscription Icon"
                            width={24}
                            height={24}
                          />
                        </div>
                        <div>
                          <h5 className="mb-0 text-white">Standard Plan</h5>
                          {!licenseData.cancelAt && (
                            <Button
                              variant="link"
                              className="p-0 text-decoration-underline"
                              style={{ color: "#ccc", fontSize: "14px" }}
                              onClick={() => navigate("/upgrade-plan")}
                            >
                              Change Plan
                            </Button>
                          )}
                        </div>
                      </div>
                      <Row className="bg-white p-2 mt-4 mx-3 rounded ">
                        <Col>
                          <div style={{ color: "#8f9a9e" }}>
                            <div style={{ fontSize: "14px", opacity: 0.8 }}>
                              Users Max
                            </div>
                            <span
                              style={{
                                fontSize: "24px",
                                fontWeight: "bold",
                                color: "#000"
                              }}
                            >
                              {licenseData.maxUsers}
                            </span>
                          </div>
                        </Col>
                        <Col xs="auto" className="my-auto">
                          <div className="text-end">
                            <div
                              style={{
                                fontSize: "14px",
                                opacity: 0.8,
                                color: "#8f9a9e"
                              }}
                            >
                              Monthly
                            </div>
                            <div
                              style={{
                                fontSize: "24px",
                                fontWeight: "bold",
                                color: "#000"
                              }}
                            >
                              ${licenseData.currentPlanPrice.toFixed(2)}
                            </div>
                          </div>
                        </Col>
                        <Col md={12}>
                          <p
                            className="my-1"
                            style={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "#000"
                            }}
                          >
                            Expires on{" "}
                            {formatDate(licenseData.currentPeriodEnd)}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* User Status Tabs */}
                  <div className="payment-tabs mb-4">
                    <Nav
                      variant="pills"
                      className="custom-tabs justify-content-center"
                      style={{
                        backgroundColor: "#f0f0f0",
                        borderRadius: "25px",
                        padding: "4px"
                      }}
                    >
                      <Nav.Item className="flex-fill">
                        <Nav.Link
                          active={activeTab === "active"}
                          onClick={() => setActiveTab("active")}
                          className="text-center"
                          style={{
                            borderRadius: "20px",
                            fontWeight: "600",
                            border: "none",
                            backgroundColor:
                              activeTab === "active" ? "white" : "transparent",
                            color: activeTab === "active" ? "#333" : "#666",
                            boxShadow:
                              activeTab === "active"
                                ? "0 2px 4px rgba(0,0,0,0.1)"
                                : "none"
                          }}
                        >
                          Active
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item className="flex-fill">
                        <Nav.Link
                          active={activeTab === "inactive"}
                          onClick={() => setActiveTab("inactive")}
                          className="text-center"
                          style={{
                            borderRadius: "20px",
                            fontWeight: "600",
                            border: "none",
                            backgroundColor:
                              activeTab === "inactive"
                                ? "white"
                                : "transparent",
                            color: activeTab === "inactive" ? "#333" : "#666",
                            boxShadow:
                              activeTab === "inactive"
                                ? "0 2px 4px rgba(0,0,0,0.1)"
                                : "none"
                          }}
                        >
                          Inactive
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </div>

                  {/* Users List */}
                  <div className="users-list">
                    {getCombinedUsers().filter((user) =>
                      activeTab === "active" ? user.isActive : !user.isActive
                    ).length === 0 ? (
                      <div
                        className="text-center py-4"
                        style={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        <p>No {activeTab} users found</p>
                      </div>
                    ) : (
                      getCombinedUsers()
                        .filter((user) =>
                          activeTab === "active"
                            ? user.isActive
                            : !user.isActive
                        )
                        .map((user, index) => (
                          <Card
                            key={user.id}
                            className={`user-card mb-3 ${
                              user.isOwner
                                ? "user-active-highlight"
                                : "user-regular"
                            }`}
                            style={{
                              border: "none",
                              borderRadius: "12px",
                              opacity: user.isActive ? 1 : 0.8,
                              backgroundColor: user.isOwner
                                ? "#e8f5e8"
                                : "#ffffff"
                            }}
                          >
                            <Card.Body className="p-3">
                              <Row className="align-items-center">
                                <Col>
                                  <div>
                                    <div
                                      style={{
                                        fontWeight: "500",
                                        color: user.isActive ? "#333" : "#666"
                                      }}
                                    >
                                      {user.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        color: "#666"
                                      }}
                                    >
                                      {user.email}
                                    </div>
                                  </div>
                                </Col>
                                <Col xs="auto">
                                  {!user.isOwner && (
                                    <div
                                      className="toggle-switch"
                                      onClick={() => toggleUserStatus(user.id)}
                                      style={{
                                        width: "50px",
                                        height: "24px",
                                        backgroundColor: user.isActive
                                          ? "#28a745"
                                          : "#ccc",
                                        borderRadius: "12px",
                                        position: "relative",
                                        cursor: "pointer"
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          backgroundColor: "white",
                                          borderRadius: "50%",
                                          position: "absolute",
                                          top: "2px",
                                          left: user.isActive ? "28px" : "2px",
                                          transition: "all 0.2s"
                                        }}
                                      />
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        ))
                    )}
                  </div>
                </Card.Body>
              </Card>

              {/* Add User Button */}
              <Button
                className="py-3 mx-3"
                style={{
                  background:
                    "linear-gradient(65.07deg, #08A45D 1.12%, #0BBE6D 98.88%)",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "white",
                  width: "calc(100% - 24px)"
                }}
                onClick={() => checkUserLimitAndShowModal()}
              >
                Add User
              </Button>

              {/* Back To App Button */}
              <div className="text-center mt-4 mb-3">
                <Button
                  variant="outline-success"
                  className="px-4 py-2"
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    borderColor: "#28a745",
                    color: "#28a745",
                    backgroundColor: "transparent"
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = "#28a745";
                    target.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = "transparent";
                    target.style.color = "#28a745";
                  }}
                  onClick={() => {
                    window.location.replace("pretrip://subscription-success");
                  }}
                >
                  Back To App
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add User Modal */}
      <Modal
        show={showAddUserModal}
        onHide={() => {
          clearFormData();
          setShowAddUserModal(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                First Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                className="py-3"
                type="text"
                placeholder="Enter first name"
                value={newUser.firstName}
                onChange={(e) =>
                  setNewUser({ ...newUser, firstName: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last name</Form.Label>
              <Form.Control
                className="py-3"
                type="text"
                placeholder="Enter last name"
                value={newUser.lastName}
                onChange={(e) =>
                  setNewUser({ ...newUser, lastName: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Email address <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                className="py-3"
                type="email"
                placeholder="Enter email address"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="flex-column gap-2">
          <Button
            variant="success"
            className="py-3 mx-3 btn btn-primary w-75 text-white fw-bold"
            onClick={handleAddUser}
            disabled={!newUser.firstName || !newUser.email}
          >
            Add User
          </Button>
          <Button
            variant="link"
            className="text-success"
            onClick={() => {
              clearFormData();
              setShowAddUserModal(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
    </div>
  );
};

export default SubscriptionDashboard;
